import { useEffect, useRef, useState } from "react";
import { AppShell } from "../../components/AppShell";
import { issueMcpCredential } from "../../lib/api";
import { authRequired, googleAllowedDomain, googleClientId } from "../../lib/env";
import { authStorageKey, clearMcpCredential, loadAuthUser, loadMcpCredential, saveMcpCredential } from "../../lib/storage";
import type { ActionState, AuthUser, GoogleCredentialResponse, McpCredential } from "../../lib/types";
import { SignInScreen } from "./SignInScreen";

/**
 * FROZEN: the Google Identity sign-in path.
 *
 * `authRequired` is false in local dev, so this whole screen is invisible during development.
 * Every bug here is a production-only bug. The script loader, the initialize() config, the
 * renderButton() width computed from getBoundingClientRect(), and the 5s "Google showed a 400
 * page" timer are all moved verbatim.
 */
export function AuthGate() {
  const buttonRef = useRef<HTMLDivElement>(null);
  const authAttemptTimer = useRef<number | null>(null);
  const [user, setUser] = useState<AuthUser | null>(() => loadAuthUser());
  const [mcpCredential, setMcpCredential] = useState<McpCredential | null>(() => loadMcpCredential());
  const [mcpCredentialMessage, setMcpCredentialMessage] = useState("");
  const [mcpCredentialState, setMcpCredentialState] = useState<ActionState>("idle");
  const [error, setError] = useState("");

  function clearAuthAttemptTimer() {
    if (authAttemptTimer.current) {
      window.clearTimeout(authAttemptTimer.current);
      authAttemptTimer.current = null;
    }
  }

  function signOut() {
    localStorage.removeItem(authStorageKey);
    clearMcpCredential();
    window.google?.accounts.id.disableAutoSelect();
    setUser(null);
    setMcpCredential(null);
    setMcpCredentialMessage("");
    setMcpCredentialState("idle");
  }

  async function mintMcpCredential(googleCredential: string) {
    if (!authRequired) return;
    try {
      setMcpCredentialState("running");
      setMcpCredentialMessage("Issuing MCP credential...");
      const nextCredential = await issueMcpCredential(googleCredential);
      saveMcpCredential(nextCredential);
      setMcpCredential(nextCredential);
      setMcpCredentialState("success");
      setMcpCredentialMessage("MCP credential ready.");
    } catch {
      setMcpCredentialState("error");
      setMcpCredentialMessage("MCP credential was not issued. Use Refresh credential in Settings.");
    }
  }

  function handleCredentialResponse(response: GoogleCredentialResponse) {
    clearAuthAttemptTimer();
    const nextUser = parseGoogleCredential(response.credential || "");
    if (!nextUser || !nextUser.email.endsWith(`@${googleAllowedDomain}`)) {
      setError(`That account is not on the ${googleAllowedDomain} domain. Sign in with your Meridian Google account.`);
      return;
    }
    localStorage.setItem(authStorageKey, JSON.stringify(nextUser));
    setUser(nextUser);
    setError("");
    void mintMcpCredential(response.credential || "");
  }

  async function setupGoogleIdentity(renderButton = false) {
    await loadGoogleIdentityScript();
    if (!window.google) return;
    window.google.accounts.id.initialize({
      client_id: googleClientId,
      callback: handleCredentialResponse,
      auto_select: false,
      context: "signin",
      hd: googleAllowedDomain,
      itp_support: true,
      ux_mode: "popup",
      use_fedcm_for_button: true
    });
    if (renderButton && buttonRef.current) {
      window.google.accounts.id.renderButton(buttonRef.current, {
        theme: "outline",
        size: "large",
        shape: "rectangular",
        text: "signin_with",
        width: Math.min(360, Math.max(260, Math.round(buttonRef.current.getBoundingClientRect().width || 320))),
        click_listener: () => {
          clearAuthAttemptTimer();
          authAttemptTimer.current = window.setTimeout(() => {
            // The old copy asserted iPhone. This timer fires on EVERY device.
            setError(
              "Google has not responded. If it showed a blank or error page, open this page in Safari or Chrome and try again."
            );
          }, 5000);
        }
      });
    }
  }

  async function refreshMcpCredential() {
    if (!authRequired) {
      setMcpCredentialState("error");
      setMcpCredentialMessage("MCP credentials are issued only on the production app.");
      return;
    }
    try {
      setMcpCredentialState("running");
      setMcpCredentialMessage("Waiting for Google confirmation...");
      await setupGoogleIdentity(false);
      window.google?.accounts.id.prompt();
      clearAuthAttemptTimer();
      authAttemptTimer.current = window.setTimeout(() => {
        setMcpCredentialState((current) => (current === "running" ? "error" : current));
        setMcpCredentialMessage((current) =>
          current === "Waiting for Google confirmation..."
            ? "Google confirmation did not complete. Try Refresh credential again."
            : current
        );
      }, 45000);
    } catch {
      setMcpCredentialState("error");
      setMcpCredentialMessage("Google confirmation could not start. Sign out and sign in again.");
    }
  }

  useEffect(() => {
    if (!authRequired || user) return;
    let cancelled = false;
    async function setupGoogle() {
      try {
        await setupGoogleIdentity(true);
        if (cancelled) return;
      } catch {
        setError("Google sign-in did not load. Check your network connection and reload the page.");
      }
    }
    void setupGoogle();
    return () => {
      cancelled = true;
      clearAuthAttemptTimer();
    };
  }, [user]);

  if (!authRequired || user) {
    return (
      <AppShell
        user={user}
        onLogout={signOut}
        mcpCredential={mcpCredential}
        mcpCredentialMessage={mcpCredentialMessage}
        mcpCredentialState={mcpCredentialState}
        refreshMcpCredential={refreshMcpCredential}
        removeMcpCredential={() => {
          clearMcpCredential();
          setMcpCredential(null);
          setMcpCredentialState("idle");
          setMcpCredentialMessage("MCP token removed from this browser.");
        }}
      />
    );
  }

  return <SignInScreen buttonRef={buttonRef} error={error} />;
}

function loadGoogleIdentityScript() {
  return new Promise<void>((resolve, reject) => {
    if (window.google) {
      resolve();
      return;
    }
    const existing = document.querySelector<HTMLScriptElement>('script[src="https://accounts.google.com/gsi/client"]');
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("Google identity failed")), { once: true });
      return;
    }
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.addEventListener("load", () => resolve(), { once: true });
    script.addEventListener("error", () => reject(new Error("Google identity failed")), { once: true });
    document.head.appendChild(script);
  });
}

function parseGoogleCredential(credential: string): AuthUser | null {
  try {
    const [, payload] = credential.split(".");
    if (!payload) return null;
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const decoded = JSON.parse(window.atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "="))) as {
      email?: string;
      name?: string;
      picture?: string;
    };
    if (!decoded.email) return null;
    return {
      email: decoded.email,
      name: decoded.name || decoded.email,
      picture: decoded.picture
    };
  } catch {
    return null;
  }
}
